<template>
  <section class="tests-tab">
    <QueryErrorNotice :error="testsTabErrors" />

    <EmptyStateNotice v-if="!tests.length && !isPending" :text="t('emptyTasks')" variant="info" />

    <div v-else class="tests-list">
      <article v-for="(item, index) in tests" :key="`test-${item.id || index}`" class="test-item">
        <button class="acc-item without-outline" type="button" @click="toggle(index)">
          <div class="question__slider text-left w-100">
            <p class="f-16 mb-0 bold collapse-title">Тест №{{ index + 1 }}</p>
          </div>
          <div class="acc-icon" :class="{ 'is-open': isOpen(index) }">
            <span class="material-icons">keyboard_arrow_down</span>
          </div>
        </button>

        <div v-if="isOpen(index)" class="test-body p-3">
          <div class="question__header mb-2">
            <h1 class="f-16 bold">{{ item.name }}</h1>
            <template v-if="!item.finish">
              <p class="f-12 bold mb-0">
                {{ t('questionInTest') }}:
                {{ item.qcount }}
              </p>
              <p class="f-12 bold mb-0">
                {{ t('timeToReady') }}: {{ item.time }} {{ breakMinuteLabel }}
              </p>
            </template>
            <p v-else class="f-12 bold mb-0">
              {{ t('testEndLabel') }}: {{ item.correctAnswer }}/{{ item.qcount }}
            </p>
          </div>
          <div v-if="roleId === 3" class="pt-2 question__btn">
            <button v-if="!item.finish" class="btn-custom f-13 submit-btn" type="button" @click="startTest(item, 'begin')">
              {{ t('start') }}
            </button>
            <button
              v-if="item.finish && item.is_repeatable"
              class="btn-custom f-13 submit-btn"
              type="button"
              @click="startTest(item, 'repeat')"
            >
              {{ t('repeatTest') }}
            </button>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import EmptyStateNotice from '@/components/common/EmptyStateNotice.vue';
import QueryErrorNotice from '@/components/common/QueryErrorNotice.vue';
import { fetchSdotQuestions, startSdotTestProcess, useTaskTestsQuery } from '@/queries/task/useTaskQueries';

const props = defineProps({
  lessonId: {
    type: Number,
    default: 0,
  },
  roleId: {
    type: Number,
    default: 3,
  },
  pupilId: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['loading-change']);

const { t, tm } = useI18n();
const router = useRouter();
const route = useRoute();

const opened = ref(new Set());
const starting = ref(false);

const testsQuery = useTaskTestsQuery(
  computed(() => Number(props.lessonId || 0)),
  computed(() => Number(props.pupilId || 0)),
  {
    retry: false,
  }
);

const tests = computed(() => (Array.isArray(testsQuery.data.value) ? testsQuery.data.value : []));
const isPending = computed(() => testsQuery.isPending.value);
const testsTabErrors = computed(() =>
  testsQuery.isError.value ? [testsQuery.error.value] : []
);
const breakMinuteLabel = computed(() => {
  const value = tm('break_text');
  return Array.isArray(value) ? String(value[1] || '') : '';
});

watch(
  () => testsQuery.isFetching.value || starting.value,
  (value) => emit('loading-change', value),
  { immediate: true }
);

watch(
  () => props.pupilId,
  () => {
    opened.value = new Set();
  }
);

function isOpen(index) {
  return opened.value.has(index);
}

function toggle(index) {
  if (opened.value.has(index)) opened.value.delete(index);
  else opened.value.add(index);
  opened.value = new Set(opened.value);
}

async function startTest(item, act) {
  if (!item?.hash) return;
  starting.value = true;
  try {
    const process = await startSdotTestProcess({
      hash: item.hash,
      lessonId: Number(props.lessonId || 0),
      act,
    });

    if (process?.rh) localStorage.setItem('rh', String(process.rh));
    if (process?.th) localStorage.setItem('th', String(process.th));

    const questions = await fetchSdotQuestions(Number(item.id || 0), 'answers');
    localStorage.setItem('task-test-questions', JSON.stringify(questions || []));
    localStorage.setItem('task-test-current', JSON.stringify(item || {}));

    if (typeof router.hasRoute === 'function' && router.hasRoute('test')) {
      router.push({
        name: 'test',
        query: {
          lessonId: String(props.lessonId || 0),
          lessonName: String(route.query.lessonName || ''),
          theme: String(route.query.theme || ''),
          day: String(route.query.day || ''),
          section: 'test',
        },
      });
    }
  } catch {
    // keep silent like legacy
  } finally {
    starting.value = false;
  }
}
</script>

<style scoped lang="scss">
.tests-list {
  margin: 0 8px;
}

.acc-item {
  width: 100%;
  border: none;
  background: #fff;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px;
  border-bottom: 1px solid #e0e0e0;
}

.question__slider p {
  color: #4f4f4f;
}

.collapse-title {
  color: #4f4f4f;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}

.acc-icon {
  color: #828282;
  transition: transform 0.2s ease;
}

.acc-icon.is-open {
  transform: rotate(180deg);
}

.test-body {
  padding: 8px;
}

.question__header h1,
.question__header p {
  color: #4f4f4f;
}

.question__btn {
  border-top: 1px dotted #bdbdbd;
}

.submit-btn {
  width: 100%;
  border: none;
  border-radius: 4px;
  background: #36b453;
  color: #fff;
  text-transform: uppercase;
  padding: 8px 12px;
}

@media screen and (min-width: 768px) {
  .tests-list {
    margin: 0 36px;
  }
}
</style>
